import { useCallback, useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import {
  BarChart3,
  Calculator,
  ClipboardList,
  Download,
  FileSpreadsheet,
  Paperclip,
  Pencil,
  PieChart,
  Plus,
  ShieldAlert,
  Tag,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { PDFDocument } from "pdf-lib";
import * as pdfjsLib from "pdfjs-dist";
import Resizer from "react-image-file-resizer";
import "./Tickets.css";
import { filesCloud, timestamp } from "../../firebase/config";
import { useAuthContext } from "../../hooks/useAuthContext";
import { useCollection } from "../../hooks/useCollection";
import { useFirestore } from "../../hooks/useFirestore";
import { useIsAdmin } from "../../hooks/useIsAdmin";

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();

const TICKET_ATTACHMENTS_ROOT = "tickets_allegati";

const TALOS_STATUSES = [
  { value: "aperto", label: "Aperto" },
  { value: "assegnato", label: "Assegnato" },
  { value: "eseguito", label: "Eseguito" },
  { value: "caricato", label: "Caricato" },
  { value: "fatturato", label: "Fatturato" },
];

const DEFAULT_FORM_DATA = {
  cliente: "MD Spa",
  descrizione: "",
  luogo: "",
  nrTicket: "",
  settoreId: "",
  tipo: "talos",
  status: "aperto",
  note: "",
  ordine: "",
  importo: "",
  dataTicket: "",
};

const parseImporto = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return NaN;

  let normalized = raw.replace(/\s/g, "").replace(/€/g, "");
  if (normalized.includes(",")) {
    normalized = normalized.replace(/\./g, "").replace(",", ".");
  }

  const n = Number(normalized);
  return Number.isFinite(n) ? n : NaN;
};

const ACCEPTED_ATTACHMENTS =
  "image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt,.zip";

const getStatusLabel = (status) =>
  TALOS_STATUSES.find((item) => item.value === status)?.label || "Da definire";

const formatEuro = (n) =>
  typeof n === "number" && Number.isFinite(n)
    ? `€ ${n.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : "--";

const toValidDate = (value) => {
  if (value == null) return null;
  let date;
  if (typeof value.toDate === "function") {
    try {
      date = value.toDate();
    } catch {
      return null;
    }
  } else if (value instanceof Date) {
    date = value;
  } else {
    date = new Date(value);
  }
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  return date;
};

const safeFormat = (value, pattern, options) => {
  const d = toValidDate(value);
  if (!d) return "";
  try {
    return format(d, pattern, options);
  } catch {
    return "";
  }
};

const computeRipartizione = (ordineRows, newTotalNumberOrNull) => {
  const rows = Array.isArray(ordineRows) ? ordineRows : [];
  const ticketsTotali = rows.length;
  const perTicket = rows.map((ticket) => {
    const parsed = parseImporto(ticket.importo);
    const importoPrima = Number.isFinite(parsed) ? parsed : 0;
    const escluso = !(importoPrima > 0);
    return {
      id: ticket.id,
      nrTicket: ticket.nrTicket || "",
      settoreId: ticket.settoreId || ticket.settore?.id || "",
      settoreNome: ticket.settoreNome || ticket.settore?.nome || "",
      importoPrima,
      importoDopo: importoPrima,
      deltaTicket: 0,
      escluso,
    };
  });

  const ticketsConImporto = perTicket.filter((t) => !t.escluso).length;
  const ticketsSenzaImporto = perTicket.length - ticketsConImporto;
  const sommaAttuale = perTicket.reduce((acc, t) => acc + t.importoPrima, 0);
  const newTotal =
    typeof newTotalNumberOrNull === "number" &&
    Number.isFinite(newTotalNumberOrNull)
      ? newTotalNumberOrNull
      : null;
  const delta = newTotal != null ? newTotal - sommaAttuale : null;
  const deltaPercent =
    newTotal != null && sommaAttuale > 0 ? (delta / sommaAttuale) * 100 : null;

  if (newTotal == null) {
    const settoreMap = new Map();
    perTicket.forEach((t) => {
      const key = `${t.settoreId}||${t.settoreNome}`;
      if (!settoreMap.has(key)) {
        settoreMap.set(key, {
          settoreId: t.settoreId,
          settoreNome: t.settoreNome,
          countTickets: 0,
          sommaPrima: 0,
          sommaDopo: 0,
        });
      }
      const s = settoreMap.get(key);
      s.countTickets += 1;
      s.sommaPrima += t.importoPrima;
      s.sommaDopo += t.importoDopo;
    });
    const perSettore = [...settoreMap.values()].map((s) => ({
      ...s,
      percentualePrima:
        sommaAttuale > 0 ? (s.sommaPrima / sommaAttuale) * 100 : 0,
      percentualeDopo:
        sommaAttuale > 0 ? (s.sommaDopo / sommaAttuale) * 100 : 0,
      deltaSettore: s.sommaDopo - s.sommaPrima,
    }));
    return {
      ticketsTotali,
      ticketsConImporto,
      ticketsSenzaImporto,
      sommaAttuale,
      newTotal,
      delta,
      deltaPercent,
      perSettore,
      perTicket,
    };
  }

  const sumValid = perTicket
    .filter((t) => !t.escluso)
    .reduce((acc, t) => acc + t.importoPrima, 0);

  if (sumValid <= 0) {
    const settoreMap = new Map();
    perTicket.forEach((t) => {
      const key = `${t.settoreId}||${t.settoreNome}`;
      if (!settoreMap.has(key)) {
        settoreMap.set(key, {
          settoreId: t.settoreId,
          settoreNome: t.settoreNome,
          countTickets: 0,
          sommaPrima: 0,
          sommaDopo: 0,
        });
      }
      const s = settoreMap.get(key);
      s.countTickets += 1;
      s.sommaPrima += t.importoPrima;
      s.sommaDopo += t.importoDopo;
    });
    const perSettore = [...settoreMap.values()].map((s) => ({
      ...s,
      percentualePrima:
        sommaAttuale > 0 ? (s.sommaPrima / sommaAttuale) * 100 : 0,
      percentualeDopo:
        sommaAttuale > 0 ? (s.sommaDopo / sommaAttuale) * 100 : 0,
      deltaSettore: s.sommaDopo - s.sommaPrima,
    }));
    return {
      ticketsTotali,
      ticketsConImporto,
      ticketsSenzaImporto,
      sommaAttuale,
      newTotal,
      delta,
      deltaPercent,
      perSettore,
      perTicket,
      error: "Nessun importo positivo per riproporzionare",
    };
  }

  const validi = perTicket.filter((t) => !t.escluso);
  validi.forEach((t) => {
    const ratio = t.importoPrima / sumValid;
    const importoDopoRaw = newTotal * ratio;
    t.importoDopo = Math.round(importoDopoRaw * 100) / 100;
  });

  const sumDopo = validi.reduce((acc, t) => acc + t.importoDopo, 0);
  const roundDiff = Math.round((newTotal - sumDopo) * 100) / 100;
  if (Math.abs(roundDiff) >= 0.005 && validi.length > 0) {
    validi[0].importoDopo =
      Math.round((validi[0].importoDopo + roundDiff) * 100) / 100;
  }

  perTicket.forEach((t) => {
    t.deltaTicket = Math.round((t.importoDopo - t.importoPrima) * 100) / 100;
  });

  const settoreMap = new Map();
  perTicket.forEach((t) => {
    const key = `${t.settoreId}||${t.settoreNome}`;
    if (!settoreMap.has(key)) {
      settoreMap.set(key, {
        settoreId: t.settoreId,
        settoreNome: t.settoreNome,
        countTickets: 0,
        sommaPrima: 0,
        sommaDopo: 0,
      });
    }
    const s = settoreMap.get(key);
    s.countTickets += 1;
    s.sommaPrima += t.importoPrima;
    s.sommaDopo += t.importoDopo;
  });

  const perSettore = [...settoreMap.values()].map((s) => ({
    ...s,
    percentualePrima:
      sommaAttuale > 0 ? (s.sommaPrima / sommaAttuale) * 100 : 0,
    percentualeDopo: newTotal > 0 ? (s.sommaDopo / newTotal) * 100 : 0,
    deltaSettore: Math.round((s.sommaDopo - s.sommaPrima) * 100) / 100,
  }));

  return {
    ticketsTotali,
    ticketsConImporto,
    ticketsSenzaImporto,
    sommaAttuale,
    newTotal,
    delta,
    deltaPercent,
    perSettore,
    perTicket,
  };
};

const formatFileSize = (bytes) => {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 KB";
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }
  return `${Math.round(bytes / 1024)} KB`;
};

const buildPendingFileId = () =>
  `pending_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

const sanitizeFilename = (filename) =>
  String(filename || "file")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9._-]/g, "_");

const getBaseFilename = (filename) =>
  sanitizeFilename(filename).replace(/\.[^.]+$/, "") || "allegato";

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const dataUrlToBlob = async (dataUrl) => {
  const response = await fetch(dataUrl);
  return await response.blob();
};

const canvasToJpegBytes = (canvas, quality) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      async (blob) => {
        try {
          if (!blob) {
            reject(new Error("JPEG_BLOB_EMPTY"));
            return;
          }
          const buf = await blob.arrayBuffer();
          resolve(new Uint8Array(buf));
        } catch (error) {
          reject(error);
        }
      },
      "image/jpeg",
      quality,
    );
  });

const compressPdfDataUrl = async (
  pdfDataUrl,
  {
    targetDpi = 170,
    jpegQuality = 0.72,
    maxPages = 25,
    pagePaddingMm = 0,
  } = {},
) => {
  const originalBlob = await dataUrlToBlob(pdfDataUrl);
  const pdfArrayBuffer = await originalBlob.arrayBuffer();
  const loadingTask = pdfjsLib.getDocument({ data: pdfArrayBuffer });
  const pdf = await loadingTask.promise;

  if (pdf.numPages > maxPages) {
    throw new Error(`PDF_TOO_LONG:${pdf.numPages}:${maxPages}`);
  }

  const outputPdf = await PDFDocument.create();
  const portrait = { width: 595.28, height: 841.89 };
  const landscape = { width: 841.89, height: 595.28 };
  const scale = targetDpi / 96;

  for (let pageIndex = 1; pageIndex <= pdf.numPages; pageIndex += 1) {
    const page = await pdf.getPage(pageIndex);
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");

    if (!context) {
      throw new Error("CANVAS_CONTEXT_NULL");
    }

    canvas.width = Math.max(1, Math.floor(viewport.width));
    canvas.height = Math.max(1, Math.floor(viewport.height));

    await page.render({
      canvasContext: context,
      viewport,
      background: "rgb(255,255,255)",
    }).promise;

    const jpegBytes = await canvasToJpegBytes(canvas, jpegQuality);
    const embeddedImage = await outputPdf.embedJpg(jpegBytes);
    const pageSize = canvas.width > canvas.height ? landscape : portrait;
    const outputPage = outputPdf.addPage([pageSize.width, pageSize.height]);
    const padding = (pagePaddingMm || 0) * 2.834645669;

    outputPage.drawImage(embeddedImage, {
      x: padding,
      y: padding,
      width: pageSize.width - padding * 2,
      height: pageSize.height - padding * 2,
    });

    canvas.width = 0;
    canvas.height = 0;
  }

  return new Blob([await outputPdf.save({ useObjectStreams: true })], {
    type: "application/pdf",
  });
};

const resizeImageFile = (file) =>
  new Promise((resolve) => {
    Resizer.imageFileResizer(
      file,
      1600,
      1600,
      "JPEG",
      78,
      0,
      (uri) => resolve(uri),
      "file",
    );
  });

const prepareAttachmentForUpload = async (file) => {
  const safeBaseName = getBaseFilename(file.name);
  const originalSize = file.size || 0;

  if (file.type?.startsWith("image/")) {
    const compressedImage = await resizeImageFile(file);
    const uploadFile = new File([compressedImage], `${safeBaseName}.jpg`, {
      type: compressedImage?.type || "image/jpeg",
    });

    return {
      fileToUpload: uploadFile,
      originalSize,
      finalSize: uploadFile.size || originalSize,
    };
  }

  if (file.type === "application/pdf") {
    const pdfDataUrl = await fileToDataUrl(file);
    let blobToUpload = file;

    try {
      const compressedPdf = await compressPdfDataUrl(pdfDataUrl, {
        targetDpi: 170,
        jpegQuality: 0.72,
        maxPages: 25,
        pagePaddingMm: 0,
      });

      if (compressedPdf.size > 0 && compressedPdf.size < originalSize) {
        blobToUpload = new File([compressedPdf], `${safeBaseName}.pdf`, {
          type: "application/pdf",
        });
      }
    } catch (error) {
      blobToUpload = file;
    }

    return {
      fileToUpload: blobToUpload,
      originalSize,
      finalSize: blobToUpload.size || originalSize,
    };
  }

  return {
    fileToUpload: file,
    originalSize,
    finalSize: originalSize,
  };
};

const buildAttachmentFilename = (file) => {
  const safeName = sanitizeFilename(file.name);
  return `${Date.now()}_${Math.random().toString(36).slice(2, 8)}_${safeName}`;
};

const buildTicketPayload = (formData, settoreOptions) => {
  const descrizione = formData.descrizione.trim();
  const luogo = formData.luogo.trim();
  const nrTicket = formData.nrTicket.trim();
  const note = formData.note.trim();
  const ordine = formData.ordine.trim();
  const importoRaw = formData.importo ?? "";
  const dataTicketRaw = formData.dataTicket ?? "";
  const selectedSector = settoreOptions.find(
    (settore) => settore.id === formData.settoreId,
  );
  const statusLabel = getStatusLabel(formData.status);

  if (!formData.status) {
    return { error: "Seleziona lo status del ticket." };
  }

  if (!descrizione) {
    return { error: "Inserisci la descrizione dell'intervento." };
  }

  if (!luogo) {
    return { error: "Inserisci il luogo dell'intervento." };
  }

  if (!nrTicket) {
    return { error: "Inserisci il numero ticket." };
  }

  if (!selectedSector) {
    return { error: "Seleziona il settore." };
  }

  let importoParsed = null;
  if (importoRaw) {
    importoParsed = parseImporto(importoRaw);
    if (Number.isNaN(importoParsed)) {
      return { error: "Importo non valido" };
    }
  }

  let dataTicketTimestamp = null;
  if (dataTicketRaw) {
    const dataTicketDate = new Date(dataTicketRaw);
    if (Number.isNaN(dataTicketDate.getTime())) {
      return { error: "Data ticket non valida." };
    }
    dataTicketTimestamp = timestamp.fromDate(dataTicketDate);
  } else {
    dataTicketTimestamp = timestamp.now();
  }

  return {
    payload: {
      cliente: "MD Spa",
      descrizione,
      luogo,
      nrTicket,
      tipo: "talos",
      tipoLabel: "Talos",
      status: formData.status,
      statusLabel,
      settore: {
        id: selectedSector.id,
        nome: selectedSector.nome,
      },
      settoreId: selectedSector.id,
      settoreNome: selectedSector.nome,
      note,
      ordine,
      importo: importoParsed,
      dataTicket: dataTicketTimestamp,
    },
  };
};

export default function Tickets() {
  const { user } = useAuthContext();
  const isAdmin = useIsAdmin(user);
  const { documents: tickets, error } = useCollection("tickets", null, [
    "createdAt",
    "desc",
  ]);
  const { documents: settoriDocs } = useCollection("settori");
  const { addDocument, deleteDocument, updateDocument, resetResponse } =
    useFirestore("tickets");

  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [attachmentsToDelete, setAttachmentsToDelete] = useState([]);
  const [formError, setFormError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState("");
  const [deletingId, setDeletingId] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [editingTicketId, setEditingTicketId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [settoreFilter, setSettoreFilter] = useState("");
  const [monthFilter, setMonthFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "createdAt",
    direction: "desc",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [selectedOrdine, setSelectedOrdine] = useState("");
  const [newTotalRaw, setNewTotalRaw] = useState("");
  const [newTotalError, setNewTotalError] = useState("");
  const [newTotalValidated, setNewTotalValidated] = useState(false);

  const settoreOptions = useMemo(
    () =>
      Array.isArray(settoriDocs)
        ? settoriDocs.map((settore) => ({
            id: settore.id,
            nome: settore.nome || settore.id,
          }))
        : [],
    [settoriDocs],
  );

  const totalTickets = Array.isArray(tickets) ? tickets.length : 0;
  const isBusy = isSubmitting || !!deletingId;

  const ordineOptions = useMemo(() => {
    const s = new Set();
    (tickets || []).forEach((t) => {
      if (t.ordine && String(t.ordine).trim()) s.add(String(t.ordine).trim());
    });
    return [...s].sort((a, b) => a.localeCompare(b, "it", { numeric: true }));
  }, [tickets]);

  const ordineSummary = useMemo(() => {
    if (!selectedOrdine || !tickets) return null;
    const rows = tickets.filter(
      (t) => t.ordine && String(t.ordine).trim() === selectedOrdine.trim(),
    );
    const parsedNewTotal =
      newTotalValidated && newTotalRaw
        ? (() => {
            const n = parseImporto(newTotalRaw);
            return Number.isFinite(n) ? n : null;
          })()
        : null;
    const r = computeRipartizione(rows, parsedNewTotal);
    return { rows, ...r };
  }, [selectedOrdine, tickets, newTotalValidated, newTotalRaw]);

  const resetForm = useCallback(() => {
    setFormData(DEFAULT_FORM_DATA);
    setExistingAttachments([]);
    setPendingAttachments([]);
    setAttachmentsToDelete([]);
    setFormError("");
  }, []);

  const closeModal = useCallback(() => {
    resetForm();
    setEditingTicketId("");
    setModalMode("create");
    setIsModalOpen(false);
  }, [resetForm]);

  const openCreateModal = () => {
    resetForm();
    setSaveSuccess("");
    setModalMode("create");
    setEditingTicketId("");
    setIsModalOpen(true);
  };

  const openEditModal = (ticket) => {
    if (!ticket?.id) return;
    setFormError("");
    setSaveSuccess("");
    setModalMode("edit");
    setEditingTicketId(ticket.id);
    setFormData({
      cliente: "MD Spa",
      descrizione: ticket.descrizione || "",
      luogo: ticket.luogo || "",
      nrTicket: ticket.nrTicket || "",
      settoreId: ticket.settore?.id || "",
      tipo: "talos",
      status: ticket.status || "aperto",
      note: ticket.note || "",
      ordine: ticket.ordine || "",
      importo:
        ticket.importo != null
          ? typeof ticket.importo === "number"
            ? ticket.importo.toFixed(2).replace(".", ",")
            : String(ticket.importo)
          : "",
      dataTicket:
        safeFormat(ticket.dataTicket, "yyyy-MM-dd") ||
        safeFormat(ticket.createdAt, "yyyy-MM-dd"),
    });
    setExistingAttachments(
      Array.isArray(ticket.attachments) ? ticket.attachments : [],
    );
    setPendingAttachments([]);
    setAttachmentsToDelete([]);
    setIsModalOpen(true);
  };

  useEffect(() => {
    if (!isModalOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeModal();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [closeModal, isModalOpen]);

  const openOrderModal = () => {
    setSelectedOrdine("");
    setNewTotalRaw("");
    setNewTotalError("");
    setNewTotalValidated(false);
    setIsOrderModalOpen(true);
  };

  const handleCalcolaRipartizione = () => {
    setNewTotalError("");
    if (!selectedOrdine) {
      setNewTotalError("Seleziona prima un ordine.");
      setNewTotalValidated(false);
      return;
    }
    if (!newTotalRaw || String(newTotalRaw).trim() === "") {
      setNewTotalError("Inserisci un importo totale.");
      setNewTotalValidated(false);
      return;
    }
    const n = parseImporto(newTotalRaw);
    if (!Number.isFinite(n) || n <= 0) {
      setNewTotalError(
        "Importo non valido. Usa formato valido come '€ 25.000,00' oppure '25000'.",
      );
      setNewTotalValidated(false);
      return;
    }
    const rows = tickets.filter(
      (t) => t.ordine && String(t.ordine).trim() === selectedOrdine.trim(),
    );
    const sumPos = rows.reduce((acc, t) => {
      const parsed = parseImporto(t.importo);
      return acc + (Number.isFinite(parsed) && parsed > 0 ? parsed : 0);
    }, 0);
    if (sumPos <= 0) {
      setNewTotalError(
        "Impossibile calcolare: nessun ticket dell'ordine ha un importo positivo valorizzato.",
      );
      setNewTotalValidated(false);
      return;
    }
    setNewTotalValidated(true);
  };

  const handleExportOrderReportCSV = () => {
    if (!selectedOrdine || !ordineSummary) return;

    const { perTicket, sommaAttuale, newTotal, delta } = ordineSummary;

    const toItaNumber = (n) =>
      typeof n === "number" && Number.isFinite(n)
        ? n.toFixed(2).replace(".", ",")
        : "";

    const header = [
      "Ordine",
      "NrTicket",
      "SettoreID",
      "SettoreNome",
      "ImportoPrima",
      "ImportoDopo",
      "Delta",
      "Note",
    ].join(";");

    const lines = [header];

    perTicket.forEach((t) => {
      const importoPrimaStr = toItaNumber(t.importoPrima);
      const importoDopoStr = newTotalValidated
        ? toItaNumber(t.importoDopo)
        : importoPrimaStr;
      const deltaStr = newTotalValidated ? toItaNumber(t.deltaTicket) : "0,00";
      const note = t.escluso ? "Escluso (importo 0/null)" : "";

      lines.push(
        [
          selectedOrdine,
          t.nrTicket || "",
          t.settoreId || "",
          t.settoreNome || "",
          importoPrimaStr,
          importoDopoStr,
          deltaStr,
          note,
        ].join(";"),
      );
    });

    lines.push("");
    lines.push("");

    const sumPrimaStr = toItaNumber(sommaAttuale);
    const sumDopoStr =
      newTotalValidated && newTotal != null
        ? toItaNumber(newTotal)
        : sumPrimaStr;
    const deltaStr =
      newTotalValidated && delta != null ? toItaNumber(delta) : "0,00";

    lines.push(
      ["TOTALE", "", "", "", sumPrimaStr, sumDopoStr, deltaStr, ""].join(";"),
    );

    const csvText = lines.join("\r\n");
    const blob = new Blob([`\uFEFF${csvText}`], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Ordine_${selectedOrdine}_${format(new Date(), "yyyyMMdd")}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const filteredTickets = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    const filtered = Array.isArray(tickets)
      ? tickets.filter((ticket) => {
          const dataTicketVal = ticket.dataTicket ?? ticket.createdAt;
          const dataTicketString = safeFormat(dataTicketVal, "d MMM yyyy");
          const dataTicketMonth = safeFormat(dataTicketVal, "yyyy-MM");

          const matchesSearch =
            !query ||
            [
              ticket.nrTicket,
              ticket.descrizione,
              ticket.luogo,
              ticket.settore?.nome,
              getStatusLabel(ticket.status),
              ticket.ordine,
              ticket.importo != null ? String(ticket.importo) : "",
              ticket.note,
              safeFormat(ticket.dataIntervento, "dd/MM/yyyy"),
              dataTicketString,
            ]
              .filter(Boolean)
              .some((value) => String(value).toLowerCase().includes(query));

          const matchesSettore =
            !settoreFilter || ticket.settore?.id === settoreFilter;

          const matchesMonth = !monthFilter || dataTicketMonth === monthFilter;

          const matchesStatus = !statusFilter || ticket.status === statusFilter;

          return (
            matchesSearch && matchesSettore && matchesMonth && matchesStatus
          );
        })
      : [];

    return [...filtered].sort((left, right) => {
      const getValue = (ticket) => {
        switch (sortConfig.key) {
          case "nrTicket":
            return ticket.nrTicket || "";
          case "status":
            return getStatusLabel(ticket.status);
          case "settore":
            return ticket.settore?.nome || "";
          case "luogo":
            return ticket.luogo || "";
          case "ordine":
            return ticket.ordine || "";
          case "importo": {
            const parsed = parseImporto(ticket.importo);
            return Number.isFinite(parsed) ? parsed : 0;
          }
          case "note":
            return ticket.note || "";
          case "dataIntervento": {
            const d = toValidDate(ticket.dataIntervento);
            return d ? d.getTime() : 0;
          }
          default: {
            const d = toValidDate(ticket.createdAt);
            return d ? d.getTime() : 0;
          }
        }
      };

      const leftValue = getValue(left);
      const rightValue = getValue(right);

      if (typeof leftValue === "number" && typeof rightValue === "number") {
        return sortConfig.direction === "asc"
          ? leftValue - rightValue
          : rightValue - leftValue;
      }

      const comparison = String(leftValue).localeCompare(
        String(rightValue),
        "it",
        {
          numeric: true,
          sensitivity: "base",
        },
      );

      return sortConfig.direction === "asc" ? comparison : -comparison;
    });
  }, [
    monthFilter,
    searchTerm,
    settoreFilter,
    sortConfig,
    statusFilter,
    tickets,
  ]);

  const handleSort = (key) => {
    setSortConfig((current) => ({
      key,
      direction:
        current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handleInputChange = (field) => (event) => {
    setFormData((current) => ({
      ...current,
      [field]: event.target.value,
    }));
  };

  const handleAttachmentSelect = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (!selectedFiles.length) return;

    setPendingAttachments((current) => [
      ...current,
      ...selectedFiles.map((file) => ({
        id: buildPendingFileId(),
        file,
      })),
    ]);

    event.target.value = "";
  };

  const handleRemovePendingAttachment = (attachmentId) => {
    setPendingAttachments((current) =>
      current.filter((item) => item.id !== attachmentId),
    );
  };

  const handleMarkAttachmentForRemoval = (attachment) => {
    setExistingAttachments((current) =>
      current.filter((item) => item.storagePath !== attachment.storagePath),
    );
    setAttachmentsToDelete((current) => [...current, attachment]);
  };

  const uploadAttachments = useCallback(async (ticketId, files) => {
    const uploadedAttachments = [];

    for (const attachment of files) {
      const preparedAttachment = await prepareAttachmentForUpload(
        attachment.file,
      );
      const uploadPath = `${TICKET_ATTACHMENTS_ROOT}/${ticketId}/${buildAttachmentFilename(
        preparedAttachment.fileToUpload,
      )}`;
      const uploadRef = filesCloud.ref(uploadPath);

      await uploadRef.put(preparedAttachment.fileToUpload);
      const downloadUrl = await uploadRef.getDownloadURL();

      uploadedAttachments.push({
        name: preparedAttachment.fileToUpload.name,
        originalName: attachment.file.name,
        mimeType:
          preparedAttachment.fileToUpload.type || attachment.file.type || "",
        size: preparedAttachment.finalSize,
        originalSize: preparedAttachment.originalSize,
        downloadUrl,
        storagePath: uploadPath,
        uploadedAt: new Date().toISOString(),
      });
    }

    return uploadedAttachments;
  }, []);

  const deleteStoredAttachments = useCallback(async (attachments) => {
    if (!attachments.length) {
      return { failedCount: 0 };
    }

    const results = await Promise.allSettled(
      attachments
        .filter((attachment) => attachment?.storagePath)
        .map((attachment) => filesCloud.ref(attachment.storagePath).delete()),
    );

    const failedCount = results.filter(
      (result) => result.status === "rejected",
    ).length;

    return { failedCount };
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    resetResponse();
    setFormError("");
    setSaveSuccess("");
    setIsSubmitting(true);

    const { payload, error: validationError } = buildTicketPayload(
      formData,
      settoreOptions,
    );

    if (validationError) {
      setFormError(validationError);
      setIsSubmitting(false);
      return;
    }

    if (modalMode === "edit") {
      let uploadedAttachments = [];

      try {
        uploadedAttachments = await uploadAttachments(
          editingTicketId,
          pendingAttachments,
        );

        const nextAttachments = [
          ...existingAttachments,
          ...uploadedAttachments,
        ];
        await updateDocument(editingTicketId, {
          ...payload,
          attachments: nextAttachments,
          attachmentCount: nextAttachments.length,
          storageFolder: `${TICKET_ATTACHMENTS_ROOT}/${editingTicketId}`,
          updatedAt: timestamp.fromDate(new Date()),
        });

        const { failedCount } =
          await deleteStoredAttachments(attachmentsToDelete);

        closeModal();
        setSaveSuccess(
          failedCount > 0
            ? `Ticket aggiornato. ${failedCount} allegati non sono stati rimossi dallo storage.`
            : "Ticket aggiornato correttamente.",
        );
      } catch (error) {
        if (uploadedAttachments.length) {
          await deleteStoredAttachments(uploadedAttachments);
        }
        setFormError("Errore durante l'aggiornamento del ticket.");
      } finally {
        setIsSubmitting(false);
      }

      return;
    }

    let createdTicket = null;

    try {
      createdTicket = await addDocument({
        ...payload,
        attachments: [],
        attachmentCount: 0,
        storageFolder: "",
        createdBy: user?.uid || "",
        createdByName: user?.displayName || "",
        updatedAt: timestamp.fromDate(new Date()),
      });

      if (!createdTicket?.id) {
        throw new Error("CREATE_TICKET_FAILED");
      }

      const uploadedAttachments = await uploadAttachments(
        createdTicket.id,
        pendingAttachments,
      );

      await updateDocument(createdTicket.id, {
        attachments: uploadedAttachments,
        attachmentCount: uploadedAttachments.length,
        storageFolder: `${TICKET_ATTACHMENTS_ROOT}/${createdTicket.id}`,
        updatedAt: timestamp.fromDate(new Date()),
      });

      closeModal();
      setSaveSuccess("Ticket salvato correttamente.");
    } catch (error) {
      if (createdTicket?.id) {
        closeModal();
        setSaveSuccess(
          "Ticket creato, ma non e stato possibile completare il caricamento degli allegati.",
        );
      } else {
        setFormError("Errore durante il salvataggio del ticket.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (ticket) => {
    const confirmed = window.confirm(
      `Vuoi eliminare il ticket ${ticket?.nrTicket || ""}?`,
    );
    if (!confirmed || !ticket?.id) return;

    resetResponse();
    setDeletingId(ticket.id);
    setSaveSuccess("");

    try {
      const { failedCount } = await deleteStoredAttachments(
        Array.isArray(ticket.attachments) ? ticket.attachments : [],
      );
      await deleteDocument(ticket.id);

      if (failedCount > 0) {
        setSaveSuccess(
          `Ticket eliminato. ${failedCount} allegati sono rimasti nello storage.`,
        );
      }
    } finally {
      setDeletingId("");
    }
  };

  if (!isAdmin) {
    return (
      <div className="tickets-page">
        <div className="tickets-empty unauthorized">
          <ShieldAlert size={18} />
          <span>Accesso riservato agli amministratori.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="tickets-page">
      <div className="tickets-header">
        <div>
          <div className="tickets-kicker">Tickets Talos</div>
          <h1>Tickets</h1>
          <p>
            Gestisci i ticket Talos per MD Spa. Traccia status, allegati,
            dettagli e numero ordine.
          </p>
        </div>
        <div className="tickets-header-actions">
          <div className="tickets-header-badge">{totalTickets} ticket</div>
          <button
            type="button"
            className="tickets-primary-btn"
            onClick={openCreateModal}
          >
            <Plus size={17} />
            Nuovo ticket
          </button>
          {isAdmin && (
            <button
              type="button"
              className="tickets-secondary-btn"
              style={{ marginLeft: 8 }}
              onClick={openOrderModal}
            >
              <PieChart size={15} />
              <span style={{ marginLeft: 6 }}>Riepilogo ordine</span>
            </button>
          )}
        </div>
      </div>

      <section className="tickets-panel">
        <div className="tickets-panel-head">
          <div className="tickets-panel-title">
            <Tag size={18} />
            <div>
              <h2>Archivio tickets</h2>
              <p>
                Vista tabellare con filtri, ordinamento e accesso rapido alla
                modifica.
              </p>
            </div>
          </div>
        </div>

        {saveSuccess && (
          <div className="tickets-inline-message success tickets-list-feedback">
            {saveSuccess}
          </div>
        )}

        {error && (
          <div className="tickets-empty error">
            Errore nel caricamento dei tickets.
          </div>
        )}

        <div className="tickets-filters">
          <div className="tickets-filter-row">
            <div className="tickets-filter-field tickets-filter-search">
              <label>Cerca</label>
              <input
                type="text"
                placeholder="Numero ticket, ordine, descrizione, status, settore..."
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                className="tickets-filter-input"
              />
            </div>

            <div className="tickets-filter-field">
              <label>Status</label>
              <select
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                className="tickets-filter-input"
              >
                <option value="">Tutti gli status</option>
                {TALOS_STATUSES.map((statusOption) => (
                  <option key={statusOption.value} value={statusOption.value}>
                    {statusOption.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="tickets-filter-field">
              <label>Settore</label>
              <select
                value={settoreFilter}
                onChange={(event) => setSettoreFilter(event.target.value)}
                className="tickets-filter-input"
              >
                <option value="">Tutti i settori</option>
                {settoreOptions.map((settore) => (
                  <option key={settore.id} value={settore.id}>
                    {settore.nome}
                  </option>
                ))}
              </select>
            </div>

            <div className="tickets-filter-field">
              <label>Periodo</label>
              <div className="tickets-filter-period">
                <input
                  type="month"
                  value={monthFilter}
                  onChange={(event) => setMonthFilter(event.target.value)}
                  className="tickets-filter-input"
                />
                <button
                  type="button"
                  className="tickets-clear-btn"
                  onClick={() => {
                    setSearchTerm("");
                    setStatusFilter("");
                    setSettoreFilter("");
                    setMonthFilter("");
                  }}
                  disabled={
                    !searchTerm &&
                    !statusFilter &&
                    !settoreFilter &&
                    !monthFilter
                  }
                  title="Pulisci filtri"
                >
                  ×
                </button>
              </div>
            </div>
          </div>
        </div>

        {!error && (!tickets || tickets.length === 0) && (
          <div className="tickets-empty">
            Nessun ticket registrato al momento.
          </div>
        )}

        {!error && Array.isArray(tickets) && tickets.length > 0 && (
          <div className="tickets-table-wrapper">
            {filteredTickets.length === 0 ? (
              <div className="tickets-empty">
                Nessun ticket corrisponde ai criteri di ricerca.
              </div>
            ) : (
              <table className="tickets-table">
                <thead>
                  <tr>
                    <th
                      className="sortable"
                      onClick={() => handleSort("nrTicket")}
                    >
                      Nr. ticket
                    </th>
                    <th
                      className="sortable"
                      onClick={() => handleSort("status")}
                    >
                      Status
                    </th>
                    <th
                      className="sortable"
                      onClick={() => handleSort("ordine")}
                    >
                      Ordine
                    </th>
                    <th
                      className="sortable"
                      onClick={() => handleSort("importo")}
                    >
                      Importo
                    </th>
                    <th
                      className="sortable"
                      onClick={() => handleSort("settore")}
                    >
                      Settore
                    </th>
                    <th
                      className="sortable"
                      onClick={() => handleSort("luogo")}
                    >
                      Luogo
                    </th>
                    <th
                      className="sortable"
                      style={{ width: 320, minWidth: 280, maxWidth: 360 }}
                      onClick={() => handleSort("note")}
                    >
                      Note
                    </th>
                    <th style={{ textAlign: "right" }}>Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTickets.map((ticket) => (
                    <tr key={ticket.id}>
                      <td className="tickets-cell-strong">
                        {ticket.nrTicket || "--"}
                      </td>
                      <td>
                        <span className="tickets-chip status">
                          {getStatusLabel(ticket.status)}
                        </span>
                      </td>
                      <td className="tickets-cell-strong">
                        {ticket.ordine || "--"}
                      </td>
                      <td className="tickets-cell-strong">
                        {ticket.importo != null
                          ? `€ ${Number(ticket.importo).toLocaleString(
                              "it-IT",
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              },
                            )}`
                          : "--"}
                      </td>
                      <td>{ticket.settore?.nome || "-"}</td>
                      <td className="tickets-cell-description">
                        {ticket.luogo || "-"}
                      </td>
                      <td
                        style={{
                          width: 320,
                          minWidth: 280,
                          maxWidth: 360,
                        }}
                      >
                        <span
                          style={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            lineHeight: "1.35",
                            fontSize: 13,
                            color: "#475569",
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                          }}
                          title={ticket.note || ""}
                        >
                          {ticket.note && String(ticket.note).trim()
                            ? ticket.note
                            : "--"}
                        </span>
                      </td>
                      <td>
                        <div className="tickets-table-actions">
                          <button
                            type="button"
                            className="tickets-secondary-btn tickets-table-btn"
                            onClick={() => openEditModal(ticket)}
                          >
                            <Pencil size={15} />
                            Modifica
                          </button>
                          <button
                            type="button"
                            className="tickets-delete-btn tickets-table-btn"
                            onClick={() => handleDelete(ticket)}
                            disabled={deletingId === ticket.id}
                          >
                            <Trash2 size={15} />
                            {deletingId === ticket.id
                              ? "Eliminazione..."
                              : "Elimina"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </section>

      {isModalOpen && (
        <div
          className="tickets-modal-backdrop"
          onClick={closeModal}
          role="presentation"
        >
          <section
            className="tickets-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="tickets-create-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="tickets-modal-head">
              <div className="tickets-panel-title">
                <ClipboardList size={18} />
                <div>
                  <h2 id="tickets-create-title">
                    {modalMode === "edit" ? "Modifica ticket" : "Nuovo ticket"}
                  </h2>
                  <p>
                    {modalMode === "edit"
                      ? "Aggiorna status, dati operativi, ordine, note e allegati."
                      : "Crea un ticket Talos per MD Spa con status, dettagli e allegati."}
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="tickets-modal-close"
                onClick={closeModal}
                aria-label="Chiudi modal"
              >
                <X size={18} />
              </button>
            </div>

            <form className="tickets-modal-form" onSubmit={handleSubmit}>
              <div className="tickets-modal-grid">
                <section className="tickets-modal-section">
                  <div className="tickets-section-header">
                    <h3>Classificazione</h3>
                    <p>Status e identificativi del ticket.</p>
                  </div>

                  <div className="tickets-form">
                    <label className="tickets-field">
                      <span>Status</span>
                      <select
                        value={formData.status}
                        onChange={handleInputChange("status")}
                      >
                        {TALOS_STATUSES.map((statusOption) => (
                          <option
                            key={statusOption.value}
                            value={statusOption.value}
                          >
                            {statusOption.label}
                          </option>
                        ))}
                      </select>
                    </label>

                    <label className="tickets-field">
                      <span>Data ticket (Data richiesta, se vuoto = oggi)</span>
                      <input
                        type="date"
                        value={formData.dataTicket}
                        onChange={handleInputChange("dataTicket")}
                      />
                    </label>

                    <label className="tickets-field">
                      <span>Data intervento (opzionale)</span>
                      <input
                        type="date"
                        value={formData.dataIntervento}
                        onChange={handleInputChange("dataIntervento")}
                      />
                    </label>

                    <label className="tickets-field">
                      <span>Nr. ordine (opzionale)</span>
                      <input
                        type="text"
                        value={formData.ordine}
                        onChange={handleInputChange("ordine")}
                        placeholder="Es. ORD-2026-0042"
                      />
                    </label>

                    <label className="tickets-field">
                      <span>
                        Importo (opzionale, formato valido con € 1.250,00 o
                        1250.50)
                      </span>
                      <input
                        type="text"
                        value={formData.importo}
                        onChange={handleInputChange("importo")}
                        placeholder="Es. 1.250,00 €"
                      />
                    </label>

                    <label className="tickets-field">
                      <span>Nr. ticket</span>
                      <input
                        type="text"
                        value={formData.nrTicket}
                        onChange={handleInputChange("nrTicket")}
                        placeholder="Es. TK-2026-015"
                      />
                    </label>

                    <label className="tickets-field">
                      <span>Settore</span>
                      <select
                        value={formData.settoreId}
                        onChange={handleInputChange("settoreId")}
                      >
                        <option value="">Seleziona settore</option>
                        {settoreOptions.map((settore) => (
                          <option key={settore.id} value={settore.id}>
                            {settore.nome}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                </section>

                <section className="tickets-modal-section">
                  <div className="tickets-section-header">
                    <h3>Dati ticket</h3>
                    <p>Cliente: MD Spa · Tipo: Talos</p>
                  </div>

                  <div className="tickets-form">
                    <label className="tickets-field">
                      <span>Luogo</span>
                      <input
                        type="text"
                        value={formData.luogo}
                        onChange={handleInputChange("luogo")}
                        placeholder="Es. Milano, Via Roma 12"
                      />
                    </label>

                    <label className="tickets-field tickets-field-full">
                      <span>Descrizione</span>
                      <textarea
                        rows="5"
                        value={formData.descrizione}
                        onChange={handleInputChange("descrizione")}
                        placeholder="Descrivi la richiesta, il problema o l'attivita da svolgere"
                      />
                    </label>

                    <label className="tickets-field tickets-field-full">
                      <span>Note (opzionali)</span>
                      <textarea
                        rows="4"
                        value={formData.note}
                        onChange={handleInputChange("note")}
                        placeholder="Eventuali dettagli aggiuntivi, appunti o informazioni di contesto"
                      />
                    </label>
                  </div>
                </section>

                <section className="tickets-modal-section tickets-modal-section-full">
                  <div className="tickets-section-header">
                    <h3>Allegati</h3>
                    <p>
                      I file vengono salvati in
                      {` ${TICKET_ATTACHMENTS_ROOT}/<ticketId>/ `}
                      con compressione automatica per immagini e PDF.
                    </p>
                  </div>

                  <label className="tickets-upload-box">
                    <input
                      type="file"
                      multiple
                      accept={ACCEPTED_ATTACHMENTS}
                      onChange={handleAttachmentSelect}
                    />
                    <Upload size={18} />
                    <div>
                      <strong>Aggiungi allegati</strong>
                      <span>
                        Trascina o seleziona immagini, PDF o documenti.
                      </span>
                    </div>
                  </label>

                  {(existingAttachments.length > 0 ||
                    pendingAttachments.length > 0) && (
                    <div className="tickets-attachments-grid">
                      {existingAttachments.map((attachment) => (
                        <div
                          key={attachment.storagePath}
                          className="tickets-attachment-card"
                        >
                          <div className="tickets-attachment-main">
                            <Paperclip size={16} />
                            <div>
                              <a
                                href={attachment.downloadUrl}
                                target="_blank"
                                rel="noreferrer"
                              >
                                {attachment.originalName || attachment.name}
                              </a>
                              <span>
                                {formatFileSize(
                                  attachment.size ||
                                    attachment.originalSize ||
                                    0,
                                )}
                              </span>
                            </div>
                          </div>
                          <button
                            type="button"
                            className="tickets-link-btn danger"
                            onClick={() =>
                              handleMarkAttachmentForRemoval(attachment)
                            }
                          >
                            Rimuovi
                          </button>
                        </div>
                      ))}

                      {pendingAttachments.map((attachment) => (
                        <div
                          key={attachment.id}
                          className="tickets-attachment-card pending"
                        >
                          <div className="tickets-attachment-main">
                            <Paperclip size={16} />
                            <div>
                              <strong>{attachment.file.name}</strong>
                              <span>
                                Nuovo allegato ·{" "}
                                {formatFileSize(attachment.file.size)}
                              </span>
                            </div>
                          </div>
                          <button
                            type="button"
                            className="tickets-link-btn"
                            onClick={() =>
                              handleRemovePendingAttachment(attachment.id)
                            }
                          >
                            Annulla
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              </div>

              <div className="tickets-form-actions">
                {formError && (
                  <div className="tickets-inline-message error">
                    {formError}
                  </div>
                )}
                <div className="tickets-form-buttons">
                  <button
                    type="button"
                    className="tickets-secondary-btn"
                    onClick={closeModal}
                    disabled={isBusy}
                  >
                    Annulla
                  </button>
                  <button
                    type="submit"
                    className="tickets-primary-btn"
                    disabled={isBusy}
                  >
                    {isBusy
                      ? modalMode === "edit"
                        ? "Aggiornamento..."
                        : "Salvataggio..."
                      : modalMode === "edit"
                        ? "Salva modifiche"
                        : "Salva ticket"}
                  </button>
                </div>
              </div>
            </form>
          </section>
        </div>
      )}

      {isOrderModalOpen && (
        <div
          className="tickets-modal-backdrop"
          onClick={() => setIsOrderModalOpen(false)}
          role="presentation"
        >
          <section
            className="tickets-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="order-summary-title"
            onClick={(event) => event.stopPropagation()}
            style={{ maxWidth: 1320, width: "calc(100% - 40px)" }}
          >
            <div className="tickets-modal-head">
              <div className="tickets-panel-title">
                <PieChart size={18} />
                <div>
                  <h2 id="order-summary-title">
                    Riepilogo ordine
                    {selectedOrdine
                      ? ` - ${selectedOrdine}`
                      : " - Seleziona ordine"}
                  </h2>
                  <p>
                    Simulatore di ripartizione proporzionale importi per ordine.
                    Nessun aggiornamento su Firestore.
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="tickets-modal-close"
                onClick={() => setIsOrderModalOpen(false)}
                aria-label="Chiudi modal riepilogo ordine"
              >
                <X size={18} />
              </button>
            </div>

            <div
              className="tickets-modal-form"
              style={{
                padding: "20px 24px",
                overflowY: "auto",
                overflowX: "hidden",
                maxHeight: "calc(100vh - 180px)",
              }}
            >
              <div style={{ display: "grid", rowGap: 22 }}>
                <section>
                  <div
                    className="tickets-section-header"
                    style={{ marginBottom: 10 }}
                  >
                    <h3
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <FileSpreadsheet size={16} />
                      A. Selezione ordine
                    </h3>
                  </div>
                  <label className="tickets-field" style={{ marginBottom: 14 }}>
                    <span className="tickets-kicker">Ordine</span>
                    <select
                      value={selectedOrdine}
                      onChange={(e) => {
                        setSelectedOrdine(e.target.value);
                        setNewTotalValidated(false);
                      }}
                    >
                      <option value="">-- Seleziona un ordine --</option>
                      {ordineOptions.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  </label>

                  {ordineSummary && (
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fit, minmax(170px, 1fr))",
                        gap: 10,
                        padding: 14,
                        background: "#f6f8fb",
                        borderRadius: 10,
                        border: "1px solid #e5e9f0",
                      }}
                    >
                      <div>
                        <div className="tickets-kicker">Totale ticket</div>
                        <div style={{ fontSize: 18, fontWeight: 700 }}>
                          {ordineSummary.ticketsTotali}
                        </div>
                      </div>
                      <div>
                        <div className="tickets-kicker">Con importo</div>
                        <div
                          style={{
                            fontSize: 18,
                            fontWeight: 700,
                            color: "#0f766e",
                          }}
                        >
                          {ordineSummary.ticketsConImporto}
                        </div>
                      </div>
                      <div>
                        <div className="tickets-kicker">Senza importo</div>
                        <div
                          style={{
                            fontSize: 18,
                            fontWeight: 700,
                            color: "#94a3b8",
                          }}
                        >
                          {ordineSummary.ticketsSenzaImporto}
                        </div>
                      </div>
                      <div>
                        <div className="tickets-kicker">Totale attuale</div>
                        <div
                          style={{
                            fontSize: 18,
                            fontWeight: 700,
                            color: "#1d4ed8",
                          }}
                        >
                          {formatEuro(ordineSummary.sommaAttuale)}
                        </div>
                      </div>
                      <div>
                        <div className="tickets-kicker">Settori coinvolti</div>
                        <div style={{ fontSize: 18, fontWeight: 700 }}>
                          {ordineSummary.perSettore.length}
                        </div>
                      </div>
                    </div>
                  )}
                </section>

                <section>
                  <div
                    className="tickets-section-header"
                    style={{ marginBottom: 10 }}
                  >
                    <h3
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <Calculator size={16} />
                      B. Nuovo importo totale (simulatore riproporziona)
                    </h3>
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr auto",
                      gap: 12,
                      alignItems: "end",
                    }}
                  >
                    <label className="tickets-field">
                      <span className="tickets-kicker">
                        Nuovo importo totale (opzionale)
                      </span>
                      <input
                        type="text"
                        value={newTotalRaw}
                        onChange={(e) => {
                          setNewTotalRaw(e.target.value);
                          setNewTotalValidated(false);
                          setNewTotalError("");
                        }}
                        placeholder="Es. 25.000,00 €"
                      />
                    </label>
                    <button
                      type="button"
                      className="tickets-primary-btn"
                      onClick={handleCalcolaRipartizione}
                      disabled={!selectedOrdine}
                    >
                      <BarChart3 size={15} />
                      <span style={{ marginLeft: 6 }}>
                        Calcola ripartizione proporzionale
                      </span>
                    </button>
                  </div>

                  {newTotalError && (
                    <div
                      style={{
                        marginTop: 12,
                        padding: "10px 14px",
                        background: "#fef2f2",
                        color: "#991b1b",
                        border: "1px solid #fecaca",
                        borderRadius: 8,
                        fontSize: 14,
                      }}
                    >
                      {newTotalError}
                    </div>
                  )}

                  {newTotalValidated &&
                    ordineSummary &&
                    !ordineSummary.error && (
                      <div
                        style={{
                          marginTop: 14,
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fit, minmax(180px, 1fr))",
                          gap: 12,
                          padding: 14,
                          background: "#eff6ff",
                          border: "1px solid #bfdbfe",
                          borderRadius: 10,
                        }}
                      >
                        <div>
                          <div className="tickets-kicker">Nuovo totale</div>
                          <div
                            style={{
                              fontSize: 17,
                              fontWeight: 700,
                              color: "#1d4ed8",
                            }}
                          >
                            {formatEuro(parseImporto(newTotalRaw))}
                          </div>
                        </div>
                        <div>
                          <div className="tickets-kicker">Delta</div>
                          <div
                            style={{
                              fontSize: 17,
                              fontWeight: 700,
                              color:
                                (ordineSummary.delta || 0) >= 0
                                  ? "#047857"
                                  : "#b91c1c",
                            }}
                          >
                            {formatEuro(ordineSummary.delta)}
                          </div>
                        </div>
                        <div>
                          <div className="tickets-kicker">Variazione</div>
                          <div
                            style={{
                              fontSize: 17,
                              fontWeight: 700,
                              color:
                                (ordineSummary.deltaPercent || 0) >= 0
                                  ? "#047857"
                                  : "#b91c1c",
                            }}
                          >
                            {typeof ordineSummary.deltaPercent === "number"
                              ? `${ordineSummary.deltaPercent >= 0 ? "+" : ""}${ordineSummary.deltaPercent.toFixed(2).replace(".", ",")} %`
                              : "--"}
                          </div>
                        </div>
                      </div>
                    )}

                  {newTotalValidated && ordineSummary?.error && (
                    <div
                      style={{
                        marginTop: 12,
                        padding: "12px 14px",
                        background: "#fef2f2",
                        color: "#991b1b",
                        border: "1px solid #fecaca",
                        borderRadius: 8,
                        fontSize: 14,
                        fontWeight: 600,
                      }}
                    >
                      ⚠️ {ordineSummary.error}
                    </div>
                  )}
                </section>

                {ordineSummary && (
                  <section>
                    <div
                      className="tickets-section-header"
                      style={{ marginBottom: 10 }}
                    >
                      <h3
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <BarChart3 size={16} />
                        C. Split per settore
                      </h3>
                    </div>
                    <div
                      className="tickets-table-wrapper"
                      style={{ marginTop: 8 }}
                    >
                      <table className="tickets-table">
                        <thead>
                          <tr>
                            <th>Settore</th>
                            <th style={{ textAlign: "center" }}>N° Ticket</th>
                            <th style={{ textAlign: "right" }}>
                              Importo prima
                            </th>
                            <th style={{ textAlign: "right" }}>% prima</th>
                            <th style={{ textAlign: "right" }}>Importo dopo</th>
                            <th style={{ textAlign: "right" }}>% dopo</th>
                            <th style={{ textAlign: "right" }}>Δ</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ordineSummary.perSettore.map((s) => (
                            <tr key={`${s.settoreId}-${s.settoreNome}`}>
                              <td className="tickets-cell-strong">
                                {s.settoreNome || s.settoreId || "--"}
                              </td>
                              <td style={{ textAlign: "center" }}>
                                {s.countTickets}
                              </td>
                              <td style={{ textAlign: "right" }}>
                                {formatEuro(s.sommaPrima)}
                              </td>
                              <td style={{ textAlign: "right" }}>
                                {s.percentualePrima
                                  .toFixed(1)
                                  .replace(".", ",")}{" "}
                                %
                              </td>
                              <td
                                style={{ textAlign: "right", fontWeight: 600 }}
                              >
                                {formatEuro(s.sommaDopo)}
                              </td>
                              <td style={{ textAlign: "right" }}>
                                {s.percentualeDopo.toFixed(1).replace(".", ",")}{" "}
                                %
                              </td>
                              <td
                                style={{
                                  textAlign: "right",
                                  color:
                                    s.deltaSettore > 0
                                      ? "#047857"
                                      : s.deltaSettore < 0
                                        ? "#b91c1c"
                                        : "inherit",
                                  fontWeight: 600,
                                }}
                              >
                                {formatEuro(s.deltaSettore)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                )}

                {ordineSummary && (
                  <section>
                    <div
                      className="tickets-section-header"
                      style={{ marginBottom: 10 }}
                    >
                      <h3
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <ClipboardList size={16} />
                        D. Dettaglio singolo ticket
                      </h3>
                    </div>
                    <div
                      style={{
                        border: "1px solid #e5e9f0",
                        borderRadius: 10,
                        overflow: "hidden",
                      }}
                    >
                      <table className="tickets-table" style={{ margin: 0 }}>
                        <thead
                          style={{ position: "sticky", top: 0, zIndex: 1 }}
                        >
                          <tr>
                            <th>Nr ticket</th>
                            <th>Settore</th>
                            <th style={{ textAlign: "right" }}>PRIMA</th>
                            <th style={{ textAlign: "right" }}>DOPO</th>
                            <th style={{ textAlign: "right" }}>Δ Ticket</th>
                            <th>Stato</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ordineSummary.perTicket.map((t) => (
                            <tr
                              key={t.id}
                              style={
                                t.escluso
                                  ? { background: "#f8fafc", color: "#64748b" }
                                  : undefined
                              }
                            >
                              <td className="tickets-cell-strong">
                                {t.nrTicket || "--"}
                              </td>
                              <td>{t.settoreNome || t.settoreId || "--"}</td>
                              <td style={{ textAlign: "right" }}>
                                {t.importoPrima > 0
                                  ? formatEuro(t.importoPrima)
                                  : "--"}
                              </td>
                              <td
                                style={{
                                  textAlign: "right",
                                  fontWeight: t.escluso ? 400 : 600,
                                }}
                              >
                                {t.importoDopo > 0
                                  ? formatEuro(t.importoDopo)
                                  : "--"}
                              </td>
                              <td
                                style={{
                                  textAlign: "right",
                                  color: t.escluso
                                    ? "#94a3b8"
                                    : t.deltaTicket > 0
                                      ? "#047857"
                                      : t.deltaTicket < 0
                                        ? "#b91c1c"
                                        : "inherit",
                                  fontWeight: t.escluso ? 400 : 600,
                                }}
                              >
                                {t.escluso || t.deltaTicket === 0
                                  ? "--"
                                  : formatEuro(t.deltaTicket)}
                              </td>
                              <td>
                                {t.escluso ? (
                                  <span
                                    className="tickets-chip"
                                    style={{
                                      background: "#f1f5f9",
                                      color: "#475569",
                                    }}
                                  >
                                    escluso dalla riproporzione
                                  </span>
                                ) : (
                                  <span
                                    className="tickets-chip status"
                                    style={{
                                      background: "#ecfdf5",
                                      color: "#047857",
                                    }}
                                  >
                                    OK
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                )}
              </div>
            </div>

            <div
              className="tickets-form-actions"
              style={{
                borderTop: "1px solid #e5e9f0",
                padding: "14px 24px",
                display: "flex",
                justifyContent: "flex-end",
                gap: 10,
              }}
            >
              <button
                type="button"
                className="tickets-secondary-btn"
                onClick={() => setIsOrderModalOpen(false)}
              >
                Chiudi
              </button>
              <button
                type="button"
                className="tickets-primary-btn"
                onClick={handleExportOrderReportCSV}
                disabled={!selectedOrdine || !ordineSummary}
              >
                <Download size={15} />
                <span style={{ marginLeft: 6 }}>Scarica report CSV</span>
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
